import { useQuery } from '@tanstack/react-query';
import { useMedplumProfile } from '@medplum/react';
import type { MedplumClient } from '@medplum/core';
import type { Practitioner, PractitionerRole, Bundle, Resource } from '@medplum/fhirtypes';

import { medplum } from '@/config/medplum';
import { usePermissions } from '@/hooks/usePermissions';

export type AuthUser = {
  practitioner: Practitioner;
  roles: PractitionerRole[];
  hasAdminAccess: boolean;
  permissions: string[];
  isSuperAdmin: boolean;
};

export const getAuthUserQueryKey = (profile?: Resource) => ['auth-user', profile];

export async function fetchAuthUser(client: MedplumClient, practitioner: Practitioner) {
  const bundle: Bundle<PractitionerRole> = await client.search('PractitionerRole', {
    practitioner: `Practitioner/${practitioner.id}`,
    active: 'true',
  });

  const roles = bundle.entry?.map((e) => e.resource!).filter(Boolean) || [];

  return { practitioner, roles };
}

export function useMedplumAuth() {
  const profile = useMedplumProfile();
  const {
    permissions,
    isSuperAdmin,
    hasAdminAccess,
    isLoading: isPermissionsLoading,
  } = usePermissions();

  const query = useQuery({
    queryKey: getAuthUserQueryKey(profile),
    queryFn: () => fetchAuthUser(medplum, profile as Practitioner),
    enabled: profile?.resourceType === 'Practitioner',
    staleTime: Number.POSITIVE_INFINITY,
  });

  const data: AuthUser | null | undefined = query.data
    ? {
        ...query.data,
        hasAdminAccess,
        permissions,
        isSuperAdmin,
      }
    : query.data;

  return {
    data,
    isLoading: query.isLoading || isPermissionsLoading,
    isPending: query.isPending || isPermissionsLoading,
    error: query.error,
    isError: query.isError,
  };
}
